import { useEffect, useRef } from 'react';
import Lenis from 'lenis';

/**
 * Initialises Lenis smooth scrolling on mount and tears it down on unmount.
 *
 * Lenis intercepts the native scroll, applies lerp-based easing, and feeds the
 * result back to `window.scrollY` so every native API (`ScrollRestoration`,
 * `IntersectionObserver`, `position: sticky`, anchor links) works unchanged.
 *
 * The tuning values below are deliberately conservative — the scroll should feel
 * like it has *weight*, not like the page is on ice. Matching the editorial,
 * tactile feel of the Chapters&Co design language.
 */
export default function useSmoothScroll() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 0.85,           // snappy & responsive easing without momentum lag
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // exponential ease-out
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1.15,
      syncTouch: false,         // keep 100% native hardware touch scroll on mobile
      touchMultiplier: 1.2,
    });

    lenisRef.current = lenis;

    // Drive Lenis from rAF
    let animId;
    const raf = (time) => {
      lenis.raf(time);
      animId = requestAnimationFrame(raf);
    };
    animId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(animId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return lenisRef;
}

